import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, CheckCircle2, Circle, MessagesSquare, User, Play, ShieldCheck, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DASHBOARD } from "@/constants/testIds";

// TODO: Replace with the real Discord invite URL. Placeholder for now.
// Discord Connect destination — always the OAuth-based flow, never the
// raw invite. Keeping a single canonical route (`/account?tab=discord`)
// means users can't end up in the server without a role.
const DISCORD_CONNECT_URL = "/account?tab=discord";

const STORAGE_KEY = "summit:onboarding-v1";

function loadState() {
  if (typeof window === "undefined") return { dismissed: false, done: [] };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { dismissed: false, done: [] };
    const parsed = JSON.parse(raw);
    return {
      dismissed: !!parsed.dismissed,
      done: Array.isArray(parsed.done) ? parsed.done : [],
    };
  } catch {
    return { dismissed: false, done: [] };
  }
}

function saveState(state) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

/**
 * First-run onboarding checklist. Persists dismissal + step completion in
 * localStorage under `summit:onboarding-v1` — no backend change required.
 * Renders as a compact card at the top of the Dashboard for new members.
 * Once dismissed, it stays dismissed on that device.
 */
export default function OnboardingChecklist({ user }) {
  const { t } = useTranslation();
  const [state, setState] = useState(loadState);

  useEffect(() => {
    saveState(state);
  }, [state]);

  // Auto-dismiss once the user finishes all five items — no reason to
  // keep pinning it to the dashboard after they've checked everything.
  // The 5 comes from the `items` array below; kept as a magic number
  // deliberately since changing it would also require re-ordering /
  // adding items downstream.
  useEffect(() => {
    if (!state.dismissed && state.done.length >= 5) {
      setState((s) => ({ ...s, dismissed: true }));
    }
  }, [state.done, state.dismissed]);

  if (state.dismissed) return null;
  const items = [
    {
      id: "profile",
      icon: User,
      label: t("onboardingChecklistUi.completeProfile"),
      subLabel: t("onboardingChecklistUi.completeProfileSub"),
      to: "/account",
    },
    {
      id: "discord",
      icon: MessagesSquare,
      label: t("onboardingChecklistUi.connectDiscord"),
      subLabel: t("onboardingChecklistUi.connectDiscordSub"),
      to: DISCORD_CONNECT_URL,
      testId: DASHBOARD.onboardingDiscord,
    },
    {
      id: "first-lesson",
      icon: Play,
      label: t("onboardingChecklistUi.watchFirstLesson"),
      subLabel: t("onboardingChecklistUi.watchFirstLessonSub"),
      to: "/courses",
    },
    {
      id: "agreement",
      icon: ShieldCheck,
      label: t("onboardingChecklistUi.readRiskAgreement"),
      subLabel: t("onboardingChecklistUi.readRiskAgreementSub"),
      to: user?.tier === "free" ? "/pricing" : "/account",
    },
    {
      id: "coach",
      icon: Sparkles,
      label: user?.tier === "premium" ? t("onboardingChecklistUi.meetProfessor") : t("onboardingChecklistUi.previewProfessor"),
      subLabel: user?.tier === "premium"
        ? t("onboardingChecklistUi.meetProfessorSub")
        : t("onboardingChecklistUi.previewProfessorSub"),
      to: "/ai-coach",
    },
  ];

  const totalDone = state.done.length;
  const totalItems = items.length;

  const toggle = (id) => {
    setState((s) => {
      const done = s.done.includes(id) ? s.done.filter((x) => x !== id) : [...s.done, id];
      return { ...s, done };
    });
  };

  const dismiss = () => setState((s) => ({ ...s, dismissed: true }));

  return (
    <section
      data-testid={DASHBOARD.onboardingChecklist}
      className="relative rounded-2xl border border-blue-900/10 bg-gradient-to-br from-blue-50 via-white to-amber-50/40 shadow-sm p-6 sm:p-8"
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid={DASHBOARD.onboardingDismiss}
        aria-label={t("onboardingChecklistUi.dismissAriaLabel")}
        className="absolute top-4 right-4 p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-white/70 transition-colors"
      >
        <X size={16} />
      </button>

      <div className="flex items-start gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold">
            {t("onboardingChecklistUi.gettingStarted")}
          </div>
          <h2 className="mt-2 font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight leading-tight">
            {t("onboardingChecklistUi.welcomeHeading", { name: user?.name?.split(" ")[0] || t("onboardingChecklistUi.defaultName") })}
          </h2>
          <p className="mt-2 text-sm text-slate-600 max-w-xl">
            {t("onboardingChecklistUi.welcomeDescription")}
          </p>
        </div>
        <div className="shrink-0 self-start rounded-full bg-white border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700">
          {t("onboardingChecklistUi.progressBadge", { done: totalDone, total: totalItems })}
        </div>
      </div>

      <ul className="mt-7 space-y-2">
        {items.map((item) => {
          const done = state.done.includes(item.id);
          const Icon = item.icon;
          const inner = (
            <div className="flex items-center gap-4 py-3 px-3 sm:px-4 rounded-lg hover:bg-white/80 border border-transparent hover:border-blue-900/10 transition-all">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  toggle(item.id);
                }}
                aria-label={done ? t("onboardingChecklistUi.markIncompleteAriaLabel", { label: item.label }) : t("onboardingChecklistUi.markCompleteAriaLabel", { label: item.label })}
                className="shrink-0"
              >
                {done ? (
                  <CheckCircle2 size={22} className="text-emerald-600" />
                ) : (
                  <Circle size={22} className="text-slate-300 group-hover:text-blue-800" />
                )}
              </button>
              <div className="min-w-0 flex-1">
                <div className={`font-semibold text-sm ${done ? "text-slate-400 line-through" : "text-slate-900"}`}>
                  {item.label}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{item.subLabel}</div>
              </div>
              <Icon size={18} className="shrink-0 text-blue-800/70" />
            </div>
          );
          if (item.href) {
            return (
              <li key={item.id} data-testid={item.testId || DASHBOARD.onboardingItem}>
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  {inner}
                </a>
              </li>
            );
          }
          return (
            <li key={item.id} data-testid={DASHBOARD.onboardingItem}>
              <Link to={item.to} className="group block">{inner}</Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
